import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { msgid, ngettext, t } from 'ttag';
import * as queryString from 'query-string';
import classNames from 'classnames';

import { ClerkActionType, ClerkCategoryType, ClerkProductClickEvent, useClerk } from '@hook/useClerk';
import { Breadcrumbs, BreadcrumbsProps } from '@components/catalog/Breadcrumbs';
import { FeedAside } from '@components/FeedAside';
import { Button } from '@ui/Button';
import { SortSelect } from '@components/SortSelect';
import FilterIcon from '@svg/ic_filter.svg';
import { Grid } from '@components/Grid';
import { Product, ProductCard } from '@components/ProductCard';
import { setProductPageReferrerToStorage, setProductReferrerPathnameToStorage, useSearchParams } from '@utils/common';
import { Pagination } from '@ui/Pagination';
import { CatalogTopBannerContainer } from '@containers/CatalogTopBanner';
import { Title } from '@ui/Title';
import { SearchSubscribeTelegramContainer } from '@containers/SearchSubscribeTelegramContainer';
import { CatalogTopLevelCategories, TopLevelCategory } from '@components/catalog/CatalogTopLevelCategories';
import { TopProducts } from '@components/catalog/TopProducts';
import SearchSubscribeTelegramTopContainer from '@containers/SearchSubscribeTelegramTopContainer';
import { SeoCatalogBlocksContainer } from '@containers/catalog/SeoCatalogBlocks';
import { ActiveFilters } from '@components/ActiveFilters';
import { ProductsFeedSleketon } from '@components/ProductsFeedSleketon';
import { useFilterData } from '@hook/productFilters';
import { PRODUCT_PAGE_REFERRER } from '@constants';
import { SeoHub, SeoHubProps } from '@components/SeoHub';

import s from './Catalog.sss';

type SeoHubData = {
  filterThematics: SeoHubProps['filters']
  seoHubItems: SeoHubProps['seoHubItems']
};

type CatalogProps = {
  productsLoading: boolean
  catalogSlug: string
  mostFrequentCatalogSlug?: string | null

  // Breadcrumbs
  breadcrumbs?: BreadcrumbsProps['breadcrumbs'] | null

  title: string
  productsCount: number

  topLevelCategories: TopLevelCategory[]

  // TOP Products
  topProducts: Product[];

  // Feed
  products: Product[];

  totalPages?: number
  seoHubData: SeoHubData
  seoHubLoaded: boolean
  className?: string
};

export const Catalog: React.FC<CatalogProps> = ({
  productsLoading,
  catalogSlug,
  mostFrequentCatalogSlug,
  breadcrumbs,
  title,
  productsCount,
  topLevelCategories,
  products,
  topProducts,
  totalPages,
  seoHubData,
  seoHubLoaded,
}) => {
  const isFirstRenderRef = useRef(true);
  const { filters } = useFilterData();
  const location = useLocation();
  const [params] = useSearchParams();
  const titleRef = useRef<HTMLTitleElement>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { sendClerkEvent } = useClerk();

  const handleProductClick = (productId: number, index: number) => {
    const referrer = filters.searchText
      ? PRODUCT_PAGE_REFERRER.CATALOG_SEARCH
      : PRODUCT_PAGE_REFERRER.CATALOG;

    sendClerkEvent<ClerkProductClickEvent>({
      category: ClerkCategoryType.goToDetail,
      action: ClerkActionType.click,
      payload: {
        itemCount: index + 1,
        productId,
        source: 'catalog',
      }
    });

    setProductPageReferrerToStorage(productId, referrer);

    setProductReferrerPathnameToStorage({
      productId,
      pathname: location.pathname,
    });
  };

  useEffect(() => {
    if (!productsLoading) {
      if (titleRef.current && !isFirstRenderRef.current) {
        titleRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      isFirstRenderRef.current = false;
    }
  }, [productsLoading]);

  const filterCatalogSlugRef = useRef<string | undefined>(undefined);
  // If search text is present, we should use most frequent catalog slug
  // but if there are not products found, then most frequent catalog is not provided
  // so we should use slug from previous render (when mostFrequentCatalogSlug was provided)
  if (filters.searchText) {
    filterCatalogSlugRef.current = mostFrequentCatalogSlug || filterCatalogSlugRef.current;
  } else {
    filterCatalogSlugRef.current = catalogSlug;
  }

  const initialLoading = productsLoading && !products.length || !seoHubLoaded;
  return (
    <div className={s.root}>
      {/* RIGHT SIDE */}
      <main className={classNames(s.content, { [s.loading]: productsLoading && products.length })}>

        {/* TITLE */}
        <div className={s.titleBlock}>
          {/* Only text should be inside h1 tag. (For seo purpose) */}
          <Title ref={titleRef} type="h1" className={s.title}>
            {title}
          </Title>
          <span className={s.titleCount}>
            {'- '}
            {ngettext(
              msgid`${productsCount} річ`,
              `${productsCount} речі`,
              `${productsCount} речей`,
              productsCount,
            )}
          </span>
        </div>

        {/* PRODUCTS FEED */}
        {initialLoading ? (
          <ProductsFeedSleketon className={s.grid} count={12} />
        ) : (
          <Grid container={true} spacing={10} className={s.grid}>
            {products.map((product, i) => (
              <Fragment key={product.id}>
                {
                  topProducts.length > 0
                  && [4, 24].includes(i)
                  && (
                    <TopProducts
                      className={s.topProductsMargin}
                      products={
                        i === 4
                          ? topProducts.slice(0, 4)
                          : topProducts.slice(4, 8)
                      }
                      showHelpLink={i === 4}  // Show link only for the first block
                    />
                  )
                }

                <Grid
                  desktop={3}
                  tablet={4}
                  def={6}
                >
                  <ProductCard
                    {...product}
                    lazyImage={i >= 4}  // No lazy images, for the first 4 products
                    onProductClick={() => handleProductClick(product.id, i)}
                  />
                </Grid>
              </Fragment>
            ))}
          </Grid>
        )}

        {!!totalPages && (
          <Pagination
            className={s.pagination}
            showEndPages={false}
            currentPage={+(params.page || 1)}
            totalPages={totalPages}
            pagesCount={3}
            hrefBuilder={
              (n) => ({
                pathname: location.pathname,
                search: queryString.stringify({
                  ...params,
                  page: n === 1 ? undefined : n,
                }),
              })
            }
          />
        )}

        {/* SUBCATEGORIES */}
        {!initialLoading && (
          <CatalogTopLevelCategories
            className={s.topLevelCategories}
            categories={topLevelCategories}
          />
        )}

        {/* SEO HUB */}
        {/*
          Always render seo hub, event during initial loading,
          because slick-slider requires warm-up which can cause CLS
        */}
        <SeoHub
          className={s.seoHub}
          filters={initialLoading ? undefined : seoHubData.filterThematics}
          seoHubItems={initialLoading ? undefined : seoHubData.seoHubItems}
        />

        <SeoCatalogBlocksContainer className={s.seoBlocks} />

        {!initialLoading && (
          <>
            <ActiveFilters
              className={s.activeFilters}
              sizeCatalogSlug={filterCatalogSlugRef.current}
            />

            {/* DESKTOP SAVED SEARCH BUTTONS  */}
            {/* DESKTOP SORTING */}
            <div className={s.desktopSortWrapper}>
              <SearchSubscribeTelegramTopContainer className={s.savedSearchTopDesktopButtons} />
              <SortSelect className={s.sort} />
            </div>
          </>
        )}

        {/* MOBILE FILTER/SORTING BUTTONS */}
        <div className={s.mobileSortingFiltering}>
          <SortSelect className={s.sort} />
          <Button
            className={s.buttonFilter}
            onClick={() => setIsFilterOpen(true)}
          >
            <FilterIcon className={s.filterButtonIcon} />
            <span className={s.filterText}>{t`Фільтри`}</span>
          </Button>
        </div>

        {/* MOBILE SAVED SEARCH BUTTONS */}
        <SearchSubscribeTelegramTopContainer className={s.savedSearchTopMobileButtons} />

        {/* BANNER */}
        <CatalogTopBannerContainer className={s.banner} />

        {/* SAVED SEARCHES BLOCK */}
        <SearchSubscribeTelegramContainer className={s.telegramSubscribe} />
      </main>

      {/* LEFT SIDE */}
      <aside className={s.side}>
        <nav className={s.breadcrumbPlaceholder}>
          {!!breadcrumbs && (
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          )}
        </nav>

        <FeedAside
          total={productsCount}
          isMobile={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          isSizeFilterEnable={true}
          isBrandFilterEnable={true}
          isColorFilterEnable={true}
          isPriceFilterEnable={true}
          isConditionFilterEnable={true}
          isCityFilterEnable={true}
          isCharacteristicFilterEnable={true}
          skip={!!(filters.searchText && productsLoading)}
          sizeCatalogSlug={filterCatalogSlugRef.current}
        />
      </aside>
    </div>
  );
};
